import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';

export default function WorkflowSettingsModal({ isOpen, onClose, settings, onSave }) {
  const [formData, setFormData] = useState({
    name: settings.name || 'Content Workflow',
    description: settings.description || '',
    logOutput: settings.logOutput || false,
    isLive: settings.isLive || false
  });

  const handleSubmit = () => {
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-dark-100 rounded-lg w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">Workflow Settings</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-dark-300 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Name Input */}
          <div>
            <label className="block text-sm font-medium mb-2">Workflow Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 bg-dark-300 border border-divider rounded focus:outline-none focus:border-accent-primary"
              placeholder="Enter workflow name"
            />
          </div>

          {/* Description Input */}
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 bg-dark-300 border border-divider rounded focus:outline-none focus:border-accent-primary h-24 resize-none"
              placeholder="Enter workflow description"
            />
          </div>

          {/* Log Output Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium">Log Output</label>
              <p className="text-sm text-gray-400">Save workflow execution logs</p>
            </div>
            <Switch
              checked={formData.logOutput}
              onChange={(checked) => setFormData(prev => ({ ...prev, logOutput: checked }))}
              className={`${
                formData.logOutput ? 'bg-accent-primary' : 'bg-dark-300'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
            >
              <span className="sr-only">Log Output</span>
              <span
                className={`${
                  formData.logOutput ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
          </div>

          {/* Production/Live Toggle with clearer status */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium">Workflow Mode</label>
              <p className="text-sm text-gray-400">
                {formData.isLive ? 'Live - Content will be published' : 'Draft - Test mode only'}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`text-sm ${!formData.isLive ? 'text-accent-primary' : 'text-gray-400'}`}>
                Draft
              </span>
              <Switch
                checked={formData.isLive}
                onChange={(checked) => setFormData(prev => ({ ...prev, isLive: checked }))}
                className={`${
                  formData.isLive ? 'bg-green-600' : 'bg-dark-300'
                } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
              >
                <span className="sr-only">Production Mode</span>
                <span
                  className={`${
                    formData.isLive ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
              <span className={`text-sm ${formData.isLive ? 'text-green-500' : 'text-gray-400'}`}>
                Live
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-colors"
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
} 